import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const getDisplayLevel = (level, pointsEarned) =>
  (level === '年會員' && (pointsEarned || 0) >= 20) ? '資深年會員' : (level || '一般顧客');

function levelPillColor(lv) {
  if (lv === '資深年會員') return '#fef3c7';
  if (lv === '年會員') return '#dbeafe';
  if (lv === '月會員') return '#dcfce7';
  if (lv === '員工') return '#ede9fe';
  return '#f1f5f9';
}

function SmallNumpad({ onPress }) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', '⌫'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginTop: 6 }}>
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onPress(k)}
          style={{
            padding: '8px 0',
            fontSize: 15,
            fontWeight: 800,
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

function pickHM(v) {
  if (!v) return '';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

/**
 * 掃描成功後的操作大彈窗：身分／點數／日券＋加扣
 * onOperate(type, value) => Promise<string msg>  type: point|pass, value: +/- number
 * todayBookings: 今日該會員預約簡表 [{ START_HM|START_AT, SEAT_NAME, NUMBER }]
 */
export default function MemberScanResultModal({
  open,
  member,
  expired,
  onClose,
  onOperate,
  onOpenLevel,
  todayBookings = [],
}) {
  const [amount, setAmount] = useState('1');
  const [amountDirty, setAmountDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('point'); // point | pass
  const [flash, setFlash] = useState(false);
  const [pointNotice, setPointNotice] = useState('');
  const [closeAsk, setCloseAsk] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount('1');
      setAmountDirty(false);
      setMsg('');
      setErr('');
      setBusy(false);
      setMode('point');
      setFlash(false);
      setPointNotice('');
      setCloseAsk(false);
    }
  }, [open, member?.ID]);

  useEffect(() => {
    if (!msg) return undefined;
    setFlash(true);
    const t = setTimeout(() => {
      setFlash(false);
      setMsg('');
    }, 2000);
    return () => clearTimeout(t);
  }, [msg]);

  const requestClose = () => {
    if (pointNotice) {
      onClose?.();
      return;
    }
    setCloseAsk(true);
  };

  const successText = pointNotice || (flash && msg ? msg : '');

  if (!open || !member) return null;

  const lv = member.DISPLAY_LEVEL || getDisplayLevel(member.LEVEL, member.POINTS_EARNED || 0);
  const expiry = member.MEMBER_EXPIRY?.split?.('T')?.[0] || '—';
  const bookings = Array.isArray(todayBookings) ? todayBookings : [];

  const handlePad = (key) => {
    if (key === '⌫') {
      setAmountDirty(true);
      setAmount((p) => String(p || '').slice(0, -1));
      return;
    }
    setAmount((p) => {
      const cur = String(p || '');
      // 預設 1 尚未改過時，按其他數字才替換；按 1 則保留，才能輸入 10、15 這類開頭是 1 的數字
      if (!amountDirty && cur === '1' && key !== '1' && key !== '00') {
        setAmountDirty(true);
        return key === '0' ? '0' : key;
      }
      setAmountDirty(true);
      if (cur === '0' && key !== '00') return key;
      return (cur + key).replace(/^0+(\d)/, '$1').slice(0, 5);
    });
  };

  const run = async (isAdd) => {
    const amt = Math.abs(parseInt(String(amount || '0'), 10) || 0);
    if (!amt) {
      setErr('請輸入數量');
      return;
    }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const val = isAdd ? amt : -amt;
      const text = await onOperate(mode, val);
      const fallback = mode === 'point'
        ? (isAdd ? `已加點 ${amt}` : `已扣點 ${amt}`)
        : (isAdd ? `已加日券 ${amt}` : `已用日券 ${amt}`);
      if (mode === 'point' && isAdd) {
        setPointNotice(text || fallback);
        setCloseAsk(false);
      } else {
        setMsg(text || fallback);
      }
      setAmount('1');
      setAmountDirty(false);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '操作失敗');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(15,23,42,.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8,
      }}
      onClick={requestClose}
    >
      <div
        style={{
          position: 'relative',
          width: 'min(620px, 96vw)',
          maxHeight: '96dvh',
          display: 'flex',
          flexDirection: 'column',
          background: successText ? '#f0fdf4' : (expired ? '#fffbeb' : '#fff'),
          borderRadius: 12,
          boxShadow: successText
            ? '0 0 0 8px #16a34a, 0 0 0 16px #dcfce7, 0 20px 50px rgba(22,163,74,.4)'
            : '0 20px 50px rgba(0,0,0,.25)',
          border: successText ? '8px solid #16a34a' : (expired ? '2px solid #f59e0b' : '1px solid #e2e8f0'),
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="關閉"
          onClick={requestClose}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 20,
            width: 40,
            height: 40,
            borderRadius: 999,
            border: '1px solid #cbd5e1',
            background: '#fff',
            color: '#64748b',
            fontSize: 22,
            fontWeight: 900,
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,.12)',
          }}
        >
          ×
        </button>

        {successText ? (
          <div
            style={{
              flex: '0 0 auto',
              background: '#16a34a',
              color: '#fff',
              fontSize: 28,
              fontWeight: 900,
              letterSpacing: 1,
              padding: '18px 52px 18px 16px',
              textAlign: 'center',
            }}
          >
            ✓ {successText}
          </div>
        ) : null}

        {closeAsk ? (
          <div
            style={{
              flex: '0 0 auto',
              background: '#fef3c7',
              color: '#92400e',
              borderBottom: '2px solid #f59e0b',
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10, textAlign: 'center' }}>
              尚未加點，要關閉嗎？
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setCloseAsk(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: 15,
                  fontWeight: 800,
                  borderRadius: 8,
                  border: '1px solid #d6d3d1',
                  background: '#fff',
                  color: '#44403c',
                  cursor: 'pointer',
                }}
              >
                返回
              </button>
              <button
                type="button"
                onClick={() => onClose?.()}
                style={{
                  padding: '8px 16px',
                  fontSize: 15,
                  fontWeight: 800,
                  borderRadius: 8,
                  border: '1px solid #b45309',
                  background: '#b45309',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                仍要關閉
              </button>
            </div>
          </div>
        ) : null}

        <div style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          padding: '12px 14px',
          minHeight: 0,
        }}
        >
        <div style={{ textAlign: 'center', marginBottom: 8, paddingRight: 36 }}>
          <div style={{
            display: 'inline-block',
            padding: '12px 24px',
            borderRadius: 999,
            fontSize: 32,
            fontWeight: 900,
            background: levelPillColor(lv),
            color: '#0f172a',
            marginBottom: 6,
            lineHeight: 1.1,
          }}
          >
            {lv}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{member.NAME || '—'}</div>
          <div style={{ fontSize: 14, color: '#475569', marginTop: 2 }}>{member.PHONE || ''}</div>
          {String(member.CARRIER || '').trim() ? (
            <div style={{
              marginTop: 8,
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '6px 10px 4px',
            }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', letterSpacing: 1 }}>載具</div>
              <Barcode
                value={String(member.CARRIER).trim()}
                format="CODE128"
                displayValue
                margin={0}
                width={1.6}
                height={48}
                fontSize={14}
                textMargin={2}
              />
            </div>
          ) : null}
        </div>

        {bookings.length > 0 && (
          <div style={{
            marginBottom: 8,
            padding: '8px 10px',
            borderRadius: 8,
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
          }}
          >
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 4, color: '#334155' }}>今日預約</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {bookings.map((b, i) => {
                const hm = b.START_HM || pickHM(b.START_AT) || '—';
                return (
                  <div key={b.ID || i} style={{ fontSize: 14, color: '#0f172a' }}>
                    <b>{hm}</b>
                    {' · '}
                    {b.SEAT_NAME || '座位'}
                    {b.NUMBER != null ? ` · ${b.NUMBER}人` : ''}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {expired && (
          <div style={{
            padding: '8px 10px',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)',
            color: '#78350f',
            fontWeight: 800,
            fontSize: 13,
            marginBottom: 8,
            textAlign: 'center',
          }}
          >
            會員已過期（{expiry}）請提醒更新會籍
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 6,
          marginBottom: 10,
        }}
        >
          <Stat label="點數" value={member.POINTS ?? 0} sub={`累計 ${member.POINTS_EARNED ?? 0}`} />
          <Stat label="日券" value={member.DAY_PASS ?? 0} />
          <Stat label="到期" value={expiry} small />
        </div>

        <div style={{
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: 10,
          background: '#f8fafc',
          marginBottom: 8,
        }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 13 }}>數量</div>
          <input
            value={amount}
            onChange={(e) => {
              setAmountDirty(true);
              setAmount(e.target.value.replace(/[^\d]/g, '').slice(0, 5));
            }}
            style={{
              width: '100%',
              fontSize: 24,
              fontWeight: 900,
              textAlign: 'center',
              padding: '8px',
              borderRadius: 8,
              border: '2px solid #94a3b8',
              boxSizing: 'border-box',
            }}
          />

          <button
            type="button"
            onClick={() => setMode((m) => (m === 'point' ? 'pass' : 'point'))}
            style={{
              width: '100%',
              marginTop: 8,
              padding: '10px 8px',
              fontSize: 15,
              fontWeight: 900,
              borderRadius: 8,
              border: '2px solid',
              borderColor: mode === 'point' ? '#16a34a' : '#2563eb',
              background: mode === 'point' ? '#dcfce7' : '#dbeafe',
              color: mode === 'point' ? '#166534' : '#1e40af',
              cursor: 'pointer',
            }}
          >
            {mode === 'point' ? '模式：點數（點此切換日券）' : '模式：日券（點此切換點數）'}
          </button>

          <SmallNumpad onPress={handlePad} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 6,
            marginTop: 8,
          }}
          >
            <OpBtn
              disabled={busy}
              color={mode === 'point' ? '#16a34a' : '#2563eb'}
              onClick={() => run(true)}
            >
              {mode === 'point' ? '＋ 加點' : '＋ 加日券'}
            </OpBtn>
            <OpBtn
              disabled={busy}
              color={mode === 'point' ? '#dc2626' : '#7c3aed'}
              onClick={() => run(false)}
            >
              {mode === 'point' ? '− 扣點' : '− 用日券'}
            </OpBtn>
          </div>
          {err && <div style={{ marginTop: 6, color: '#b91c1c', fontWeight: 800, fontSize: 13 }}>{err}</div>}
        </div>

        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {onOpenLevel && (
            <button type="button" onClick={onOpenLevel} style={secBtn}>調整身分</button>
          )}
          <button type="button" onClick={requestClose} style={{ ...secBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>
            關閉
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, small }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '8px 6px',
      textAlign: 'center',
    }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 900, marginTop: 2 }}>{value}</div>
      {sub != null && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function OpBtn({ children, onClick, color, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '10px 6px',
        fontSize: 14,
        fontWeight: 900,
        color: '#fff',
        background: color,
        border: 'none',
        borderRadius: 8,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

const secBtn = {
  padding: '8px 14px',
  fontSize: 14,
  fontWeight: 700,
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
};
